import { ButtonV2 } from '@synoptic/ui-kit/button-v2/button-v2.js';
import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import { SettingsIcon } from '@synoptic/ui-kit/icons/react/settings.js';
import { SoundHighIcon } from '@synoptic/ui-kit/icons/react/sound-high.js';
import { SoundOffIcon } from '@synoptic/ui-kit/icons/react/sound-off.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { FC, useState } from 'react';
import { SoundSettingsModal } from '@/components/status-bar/sound-settings-modal';
import {
  getStreamNotifSoundStatus,
  setStreamNotifSoundStatus,
} from '@/components/streams/toggle-notif-sound/stream-notif-sound-status.ts';
import { SoundButton } from '@/components/toggle-sound/sound-button.tsx';
import { SoundTrack } from '@/components/toggle-sound/sound-settings';
import { SoundStatus } from '@/components/toggle-sound/sound-status.ts';
import {
  useStreamSoundSettings,
  setStreamSoundTrack,
  setStreamSoundVolume,
} from './stream-sound-settings';
import { soundButtonContainer } from './stream-sound-button.css';

const useStreamNotificationSound = (streamId: string) => {
  const [currentSoundStatus, setCurrentSoundStatus] = useState(
    getStreamNotifSoundStatus(streamId),
  );
  const soundSettings = useStreamSoundSettings(streamId);

  const onToggleSound = () => {
    const newStatus =
      currentSoundStatus === SoundStatus.on ? SoundStatus.off : SoundStatus.on;

    setStreamNotifSoundStatus(newStatus, streamId);
    setCurrentSoundStatus(newStatus);
  };

  const handleTrackChange = (track: SoundTrack) => {
    setStreamSoundTrack(streamId, track);
  };

  const handleVolumeChange = (volume: number) => {
    setStreamSoundVolume(streamId, volume);
  };

  return {
    currentSoundStatus,
    soundSettings,
    onToggleSound,
    handleTrackChange,
    handleVolumeChange,
  };
};

type StreamNotificationSound = ReturnType<typeof useStreamNotificationSound>;

const StreamSoundSettingsModal = ({
  open,
  onOpenChange,
  sound,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sound: StreamNotificationSound;
}) => (
  <SoundSettingsModal
    open={open}
    onOpenChange={onOpenChange}
    settings={sound.soundSettings}
    soundStatus={sound.currentSoundStatus}
    showHighlightSettings={false}
    onToggleSound={sound.onToggleSound}
    onTrackChange={sound.handleTrackChange}
    onVolumeChange={sound.handleVolumeChange}
  />
);

export const StreamSoundButton: FC<{ streamId: string }> = ({ streamId }) => {
  const isTablet = useIsTablet();
  const [showSettings, setShowSettings] = useState(false);
  const sound = useStreamNotificationSound(streamId);

  return (
    <>
      <div className={soundButtonContainer}>
        <SoundButton
          soundStatus={sound.currentSoundStatus}
          onClick={sound.onToggleSound}
        />
        <IconButton
          variant="secondary"
          rounded={false}
          size={isTablet ? 'medium' : 'small'}
          onClick={() => setShowSettings(true)}
          aria-label="Sound settings"
        >
          <SettingsIcon />
        </IconButton>
      </div>

      <StreamSoundSettingsModal
        open={showSettings}
        onOpenChange={setShowSettings}
        sound={sound}
      />
    </>
  );
};

export const StreamSoundButtonV2: FC<{
  streamId: string;
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;
}> = ({ streamId, settingsOpen, onSettingsOpenChange }) => {
  const sound = useStreamNotificationSound(streamId);
  const soundOn = sound.currentSoundStatus === SoundStatus.on;

  return (
    <>
      <ButtonV2
        type="button"
        variant="secondary"
        endIcon={soundOn ? <SoundHighIcon /> : <SoundOffIcon />}
        onClick={sound.onToggleSound}
      >
        {soundOn ? 'Sound on' : 'Sound off'}
      </ButtonV2>
      <StreamSoundSettingsModal
        open={settingsOpen}
        onOpenChange={onSettingsOpenChange}
        sound={sound}
      />
    </>
  );
};
